import { useEffect, useRef, useState } from "react";
import api, { mediaUrl, formatApiErrorDetail } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAutoGrow } from "../lib/useAutoGrow";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { initialsFrom } from "../lib/utils";
import { resizePhotoToDataUrl } from "../lib/profilePhoto";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { AlertTriangle, Save } from "lucide-react";
import { SecondaryAction } from "../components/kit/SecondaryAction";
import { resolveSaveErrorMessage } from "../lib/settingsErrorMessages";

/**
 * The player's cricket profile — the Player profile section of `/settings`
 * (issue #338), and step 1 of the first-run walkthrough (issue #289).
 *
 * #338 MOVED THE FIRST OF THOSE, AND ONLY THAT. Until #338 this component
 * was what `/profile` rendered for a player, via Profile.jsx's role
 * dispatch. Martin's decision 4 on #287 merged that page into Settings, so
 * `/profile` is now a redirect and Settings renders this component
 * directly. Nothing about the component's contract changed for the
 * walkthrough: it is still rendered standalone, still owns its own fetch
 * and save, and still knows nothing about either host.
 *
 * THE `embedded` PROPS EXIST SO THERE IS ONE COPY OF THIS FORM. The
 * ticket is explicit that the walkthrough must reuse the existing pages
 * rather than build "third and fourth copies of the same forms" — a second
 * profile form would drift from this one field by field, and every future
 * change would have to be made twice by someone who remembered there were
 * two. So the walkthrough renders THIS component and supplies its own
 * chrome:
 *
 *   `embedded`      — drop the page heading and the Save button, because the
 *                     wrapper provides both a step heading and a Continue
 *                     control that saves. Two save buttons on one screen is
 *                     the confusion, not the convenience.
 *   `controlRef`    — the wrapper's handle on `save()`, so Continue means
 *                     "save and go on" rather than "hope they pressed Save
 *                     first". Assigned on every render so it never closes
 *                     over a stale `player`.
 *   `onStateChange` — the current form state, pushed up on load and on every
 *                     edit, so the wrapper can enable Continue the moment the
 *                     step-1 bar is met (lib/onboarding.js) instead of after
 *                     a round trip. It receives the player record itself, so
 *                     the load state travels separately:
 *   `onLoadState`   — #1077: `true` while the GET /players/me has failed and
 *                     not yet been retried; the retry is `controlRef.current
 *                     .reload()`. A second callback rather than extra fields
 *                     on `onStateChange`'s argument, which is the player
 *                     record every host already hands to
 *                     `isProfileStepComplete`.
 *
 * Every one of them is optional and inert in Settings, which renders
 * exactly the form `/profile` used to.
 *
 * `showHeading` is the one thing #338 needed and `embedded` could not
 * express. `embedded` conflates two decisions — drop the page heading AND
 * drop the Save button — because the walkthrough wants both. Settings wants
 * the first and not the second: it supplies its own section heading (one
 * <h1> per page, and Settings already owns it), but the Save button is the
 * whole point of the card. So the two are separate props rather than an
 * `embedded="settings"` mode that would make the walkthrough's call site
 * read as a string comparison.
 *
 * `frameless` is the third narrow prop, for the same reason the first two
 * are narrow. #456's one-area decision (Andreas, 2026-08-27) merged Account
 * and this profile into ONE card in Settings — so its host there
 * (components/AccountProfileCard.jsx) provides the bordered chrome, and
 * this component rendering its own `<Card>` inside it would nest a bordered
 * card in a bordered card, which reads as a rendering mistake. `frameless`
 * swaps the card shell for a plain `<div>` and changes NOTHING else: same
 * single-column stack (#508's CoachProfileCard-parity layout), same fields,
 * same testids, same save button. The walkthrough still renders the framed
 * version on its bare step surface, which is why this is a prop and not a
 * change to the default.
 *
 * `photoLast` (#785, review F3) — a fourth narrow prop, named for exactly
 * what it does rather than inferred from a combination of the other three.
 * An earlier version of this fix read the reorder off `embedded &&
 * frameless`, which happened to select only `OnboardingNew.jsx` today, but
 * neither half of that pair MEANS "put the photo last": `embedded` means
 * "host owns the page chrome and the save control", `frameless` means "no
 * card shell". Two hosts could plausibly want either combination someday
 * without wanting this reorder, and a reader of a two-flag `&&` has no way
 * to tell it apart from an accident of which callers currently exist. The
 * DECISION this prop encodes, recorded on #785 and not only here: the
 * drawing (`SetUp.dc.html`) puts the photo LAST because the walkthrough
 * asks nine questions before an optional extra it has already told the
 * player is optional; Settings keeps the photo FIRST because that is
 * #478's own deliberate reversal of the old "no photo" line (see
 * `CoachProfileCard.jsx`) and the parity #508 built this stack to match.
 * Two hosts, two genuinely different answers to "where does the photo go" —
 * `photoLast` is the one prop that says which, explicitly, at every call
 * site, rather than being derived.
 *
 * `kitType` (issue #830) — a fifth narrow prop, and the only one that
 * changes TYPE rather than layout. Default `false` renders exactly what
 * this file has always rendered: shadcn `<Label>`/`<Input>`/`<Select>`,
 * Tailwind `text-sm`/`text-xs`/`text-base`, unmodified — Settings (via
 * `AccountProfileCard.jsx`) and the shipped `Onboarding.jsx` both call this
 * component with no `kitType` at all, so both are byte-identical before and
 * after this prop existed (`AccountProfileCard.test.js`'s own pinned call
 * site, `<PlayerProfile showHeading={false} frameless />`, is the proof —
 * it does not mention `kitType` and would fail if a caller of this PR ever
 * added one there). `true` (`OnboardingNew.jsx`, the only caller that sets
 * it) is #830's fix for the finding filed against THIS file: the embedded
 * form's labels/inputs/selects were still Tailwind `rem`, 19 of the 25
 * marked elements #797's own check found moving on `/onboarding`.
 *
 * #868 (G5.1/G5.2) WIDENED THIS PROP ONCE, ON PURPOSE. The Club/Team
 * placeholders (D41) and the type-ramp height fix stay type-only, exactly
 * as above. The photo panel (`kitPhotoPanel` vs `legacyPhotoPanel`, below)
 * does not: SetUp.dc.html draws a bordered card with a plain 40px circle
 * and "Add a photo" as navy text, not the 56px ringed avatar + outline
 * `Button` Settings still uses, and no font-size override makes one read as
 * the other. `kitType` was already the one flag scoped to this exact call
 * site, so it is what this structural swap keys off rather than adding a
 * SIXTH narrow prop for a screen that already has five — `kitType={false}`
 * still renders `legacyPhotoPanel`, byte-identical, everywhere else.
 *
 * THE MECHANISM IS CSS, NOT A SECOND CLASS LIST ON EVERY FIELD. Every
 * `<Label>`/`<Input>`/`<Select>` call site below keeps its EXACT existing
 * className — `PlayerProfile.rhythm.test.js` pins several of them
 * verbatim (`<Label>{label}</Label>`, `data-testid="pp-name"
 * className="h-11"`, `data-testid="pp-batting" className="h-11"`,
 * `data-testid="pp-role" className="h-11"`) as proof this form still
 * matches `CoachProfileCard`'s own rhythm, a claim `kitType` does not
 * change and must not have to re-prove field by field. Instead, `kitType`
 * adds ONE conditional attribute to this component's own root —
 * `data-pp-kit-type="true"`, present only when this prop is — and
 * `index.css` carries the actual type-ramp overrides as attribute-scoped
 * rules (search that file for `data-pp-kit-type`), each one a fixed-px
 * value pulled from the same `--nw-*` tokens / kit sizes `CoachSetUp.jsx`
 * already uses for its own text. `kitType={false}` renders the root with
 * no such attribute at all, so those rules never match and the DOM is
 * exactly what it always was — no attribute, no class, no inline style
 * added anywhere in this file's own output.
 */
export default function PlayerProfile({
  embedded = false,
  showHeading = true,
  frameless = false,
  photoLast = false,
  kitType = false,
  controlRef = null,
  onStateChange = null,
  onLoadState = null,
  hidePhoto = false,
  onSaved = null,
}) {
  const { user, updateUser } = useAuth();
  const [player, setPlayer] = useState(null);
  // #1077 — a failed GET /players/me, kept apart from `player` so "could not
  // read it" is never the same value as "no profile yet" (`{}`). Reported to
  // the host through `onLoadState`, and `reload` through `controlRef`; no
  // visual change in here — only a host that asks (OnboardingNew) renders it.
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  // #1131 — the flag-on Settings host (`kitType`, not `embedded`) shows a
  // failed save ON THE PAGE (D57: no toast on the new look). The set-up host
  // (`kitType` + `embedded`) renders its own banner from `save()`'s `false`.
  const [saveError, setSaveError] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);
  // #868 (D172 S1) — Continue stays full gold and `aria-disabled` while step
  // 1 is incomplete; a tap has to move focus to the first field still
  // missing rather than doing nothing. `battingRef`/`roleRef` are the two
  // Radix `SelectTrigger`s the completeness rule (`isProfileStepComplete`,
  // lib/onboarding.js) actually cares about — `focusFirstMissing` below is
  // exposed on `controlRef` so `OnboardingNew.jsx` never has to reach past
  // this component's own fields.
  const battingRef = useRef(null);
  const roleRef = useRef(null);
  // #1069 — kitType only: Club and Team wrap and grow instead of clipping
  // (SetUp.dc.html:88-91 / Settings.dc.html draw `min-height:46px`, no
  // nowrap, no ellipsis). Same hook the coach Headline/Teams use (#1014).
  const clubRef = useRef(null);
  const schoolRef = useRef(null);
  useAutoGrow(clubRef, player?.club);
  useAutoGrow(schoolRef, player?.school);

  const load = () =>
    api
      .get("/players/me")
      .then((r) => setPlayer(r.data))
      .catch(() => {
        setPlayer({});
        setLoadFailed(true);
      });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reload = () => {
    setLoadFailed(false);
    setPlayer(null);
    load();
  };

  useEffect(() => {
    if (onLoadState) onLoadState(loadFailed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadFailed]);

  useEffect(() => {
    if (onStateChange) onStateChange(player);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  if (!player) return <div className="text-slate-500">Loading…</div>;

  const set = (k, v) => {
    if (saveError) setSaveError("");
    setPlayer({ ...player, [k]: v });
  };

  // "" when the player has no photo (mediaUrl passes absolute URLs through
  // and resolves stored paths against the API host), which is what lets the
  // AvatarFallback initials render instead of a stranger's face — #294.
  const photoUrl = mediaUrl(player.profile_photo);

  // The photo's OWN save path (#294) — POST/DELETE /players/me/photo, never
  // the form's PUT (whose whole-object save is the eight-field profile).
  // `updateUser` pushes the server's stored value into auth context, which
  // repaints the topbar avatar immediately — AccountMenu reads
  // `user.profile_photo`, so that is a data change there, not a code change.
  // Mirrors CoachProfileCard's photo handlers exactly.
  const applyPhoto = (photo) => {
    setPlayer((p) => ({ ...p, profile_photo: photo }));
    if (user) updateUser({ ...user, profile_photo: photo });
  };

  const changePhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      // Client-side resize to a 256px square (WebP, JPEG where the canvas
      // can't encode WebP) BEFORE upload — a phone photo is multi-MB and the
      // API's cap is ~30KB. The server re-validates size and content type
      // regardless; this is convenience, not the boundary.
      const photo = await resizePhotoToDataUrl(file);
      const { data } = await api.post("/players/me/photo", { photo });
      applyPhoto(data.profile_photo);
      toast.success("Photo updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Photo upload failed");
    }
    setPhotoBusy(false);
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      const { data } = await api.delete("/players/me/photo");
      applyPhoto(data.profile_photo ?? null);
      toast.success("Photo removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove the photo");
    }
    setPhotoBusy(false);
  };

  const save = async () => {
    if (!player.player_id) {
      // #1077 — only the set-up host (`embedded` + `kitType`, OnboardingNew)
      // shows this on the page, via its own `save()`-returned-false banner
      // (D57). `kitType` alone is NOT that host: the flag-on Settings
      // (the former AccountProfileCardNew, now SettingsScreen) mounts `kitType` with its own Save and no
      // banner, so it and every other host keep the toast as before.
      if (!(kitType && embedded)) toast.error("No player profile yet");
      return false;
    }
    setBusy(true);
    setSaveError("");
    try {
      // #1406: this form never owns the photo, on any host. The photo saves
      // through its own POST/DELETE routes, so the `profile_photo` this form
      // loaded can be stale (a photo uploaded on another device or tab since)
      // and echoing it, or its `null`, wiped the newer photo. #1135 did this
      // for the Settings host that hides the panel; it holds for all of them.
      const { data } = await api.put(
        `/players/${player.player_id}`,
        Object.fromEntries(Object.entries(player).filter(([k]) => k !== "profile_photo")),
      );
      setPlayer(hidePhoto ? { ...data, profile_photo: player.profile_photo } : data);
      if (onSaved) onSaved(data);
      // In the walkthrough the step advancing IS the confirmation; a toast
      // on top of it is noise on a screen the player is already leaving.
      if (!embedded) toast.success("Profile updated!");
      setBusy(false);
      return true;
    } catch (e) {
      // #868 (D172 S6) — the walkthrough shows this failure ON THE PAGE
      // (D57, with D55's ink-text rule), never as a toast; `kitType` is the
      // one flag scoped to that one caller (OnboardingNew.jsx), same as
      // every other kit-only branch in this file, so Settings and the
      // shipped Onboarding.jsx keep this toast exactly as before —
      // `OnboardingNew.jsx`'s own `continueFromProfile` renders the
      // on-page message from this `save()` call's `false` return.
      // #1131 — the Settings host (`kitType`, not `embedded`) has no banner
      // of its own, so this component draws one (Settings.dc.html's
      // failure-message style, the card's own "Save failed" word).
      if (!kitType) toast.error("Save failed");
      else if (!embedded) setSaveError(resolveSaveErrorMessage(e));
      setBusy(false);
      return false;
    }
  };

  // #868 (D172 S1) — mirrors `isProfileStepComplete` (lib/onboarding.js)
  // field-for-field rather than importing it: that helper takes the API's
  // snake_case shape (which `player` already is) and returns a bool, not
  // which field to blame, and duplicating the two checks it already makes
  // is smaller than adding a second return shape to a module three other
  // files also import unchanged. DOM order, not rule order: batting style
  // sits before playing role on this form, so an incomplete pair
  // (`batting_style` AND `bowling_style` both empty) is "the first missing
  // field" even when playing role happens to be empty too.
  const focusFirstMissing = () => {
    const filled = (v) => typeof v === "string" && v.trim() !== "";
    if (!filled(player.batting_style) && !filled(player.bowling_style)) {
      battingRef.current?.focus();
    } else if (!filled(player.playing_role)) {
      roleRef.current?.focus();
    }
  };

  // The wrapper's Continue calls this. Reassigned every render deliberately:
  // `save` closes over `player`, so a ref set once in an effect would save
  // whatever the form held when it mounted.
  if (controlRef) controlRef.current = { save, busy, focusFirstMissing, reload };

  // The two panels' CONTENT, shared verbatim by the framed and frameless
  // shells below — extracting it is what keeps `frameless` a chrome-only
  // prop rather than a second copy of the form.
  //
  // #508 — the photo block now matches CoachProfileCard's rhythm exactly:
  // a small h-14 avatar to the LEFT of its own Change/Remove controls,
  // instead of the old centred 128px portrait with a name-and-badge preview
  // stacked under it. The coach editor has no such preview and this parity
  // pass drops it here too; the same fields, testids and OWN save route
  // (#294 — POST/DELETE /players/me/photo, never the form's PUT) are
  // untouched. The photo input and its `initialsFrom` fallback stay in THIS
  // file on purpose: PlayerProfile.photo.test.js pins them here at source
  // level, and a shared photo component would move those strings out of
  // both editors and force rewriting the coach's pinned test too — the
  // churn #508 says the coach must not take. The genuinely shared shell
  // that DID fall out is the read-only `ProfileFieldList` (the views).
  // #294 — the upload gesture, built the same way as the coach headshot
  // (#478/#499's option B): the picked file is resized on-device to a
  // 256px WebP and saved through its OWN route (POST/DELETE
  // /players/me/photo), not the eight-field form save. Shared verbatim by
  // both photo panels below — the input element is invisible either way,
  // so there is exactly one of it, not one per variant.
  const photoFileInput = (
    <input
      ref={fileRef}
      type="file"
      accept="image/jpeg,image/png,image/webp"
      className="hidden"
      data-testid="pp-photo-input"
      onChange={(e) => {
        changePhoto(e.target.files?.[0]);
        // Same file picked twice must fire onChange twice.
        e.target.value = "";
      }}
    />
  );

  const legacyPhotoPanel = (
    <div className="flex items-center gap-3" data-testid="pp-photo-controls">
      {/* #785 — the gold ring comes off. SetUp.dc.html:60 draws this circle
          plain (a neutral fill and a 1px slate border), and #780's review
          named this avatar as the last remaining breach of R-003 (one gold
          element per screen): it and the walkthrough's own Continue/step
          gold read as a matched pair on a 390px screen rather than one
          accent. border-slate-200 is the kit's own neutral ring colour
          (#E2E8F0, the same value the drawing's empty circle uses) — not a
          new colour choice. This is the shared avatar (Settings renders it
          too), so the ring comes off everywhere, not just the walkthrough:
          R-003 is a screen rule, and nothing makes a second gold ring
          correct on Settings that isn't already correct here. */}
      <Avatar className="h-14 w-14 shrink-0 border-4 border-slate-200">
        {/* Issue #294. Initials while there is no photo — NEVER the
            hardcoded Pexels stranger the original bug fell through to. */}
        {photoUrl && <AvatarImage src={photoUrl} alt="" />}
        <AvatarFallback className="text-sm font-semibold">
          {initialsFrom(player.full_name) || "P"}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {photoFileInput}
          <Button
            type="button"
            variant="outline"
            className="h-10"
            disabled={photoBusy}
            onClick={() => fileRef.current?.click()}
            data-testid="pp-photo-change"
          >
            {photoBusy ? "Working…" : player.profile_photo ? "Change photo" : "Add a photo"}
          </Button>
          {player.profile_photo && (
            <button
              type="button"
              onClick={removePhoto}
              disabled={photoBusy}
              className="text-xs text-slate-500 hover:text-red-600 underline"
              data-testid="pp-photo-remove"
            >
              Remove photo
            </button>
          )}
        </div>
        {/* #785 — the hint's second sentence returns to SetUp.dc.html's own
            ("Not required."), the reassurance the walkthrough's lede already
            echoes ("A photo is not required."). Shared with Settings: this
            is a two-sentence hint, not a layout rule, and there is no
            reading of it where "not required" is true in the walkthrough
            and false in Settings — the field's optionality does not change
            by host. The dropped "resized on your device" detail is still
            true, just no longer said here; it was never load-bearing copy
            (the resize itself is unconditional, in lib/profilePhoto.js). */}
        <p className="text-xs text-slate-400 mt-1">
          Shown small, next to your profile. Not required.
        </p>
      </div>
    </div>
  );

  // #868 (G5.2) — SetUp.dc.html draws this control as a CARD (min 60px,
  // 10px/14px padding, 1px `--nw-line`, radius 12), not the shadcn outline
  // `Button` + 56px ring avatar above: an empty 40px circle
  // (`--nw-line-faint` fill, 1px `--nw-line` border — plain, never showing
  // initials, matching the drawing's genuinely empty circle rather than
  // guessing a filled one for a state the artboard never draws), "Add a
  // photo" as 14px/600 navy TEXT (not a bordered button), and the same
  // two-sentence hint at 11px. `kitType` already means "this call site gets
  // the restyle" everywhere else on this file (its own docstring); this is
  // the one place that restyle is structural rather than type-only, because
  // #868's finding against this exact block is structural, not a font
  // size — Settings keeps `legacyPhotoPanel`, byte-identical, because it
  // never sets `kitType`. Same `pp-photo-input`/`pp-photo-change`/
  // `pp-photo-remove` testids and the same `changePhoto`/`removePhoto`
  // routes (#294) — only the chrome around them changed.
  const kitPhotoPanel = (
    <div
      data-testid="pp-photo-controls"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        minHeight: 60,
        padding: "10px 14px",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-card)",
      }}
    >
      <span
        style={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "var(--nw-radius-pill)",
          background: "var(--nw-line-faint)",
          border: "1px solid var(--nw-line)",
          overflow: "hidden",
          display: "flex",
        }}
      >
        {photoUrl && <img src={photoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        {photoFileInput}
        <SecondaryAction
          variant="link"
          disabled={photoBusy}
          onClick={() => fileRef.current?.click()}
          data-testid="pp-photo-change"
          style={{
            justifyContent: "flex-start",
            textAlign: "left",
            // D237: the box grows up only. The Remove link sits 2px below, and a
            // symmetric box would be painted over by it; the bottom margin is not
            // pulled back, so the content below moves down by the half-leading.
            marginBottom: 0,
            "--nw-tap-lh": 1.4, // this row's own line height, for browsers without `lh`
            cursor: photoBusy ? "default" : "pointer",
          }}
        >
          {photoBusy ? "Working…" : player.profile_photo ? "Change photo" : "Add a photo"}
        </SecondaryAction>
        {player.profile_photo && (
          <button
            type="button"
            onClick={removePhoto}
            disabled={photoBusy}
            data-testid="pp-photo-remove"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              margin: 0,
              textAlign: "left",
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              fontSize: 11,
              color: "var(--nw-ink-muted)",
              textDecoration: "underline",
              cursor: photoBusy ? "default" : "pointer",
            }}
          >
            Remove photo
          </button>
        )}
        <span style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", fontSize: 11, color: "var(--nw-ink-muted)" }}>
          Shown small, next to your profile. Not required.
        </span>
      </div>
    </div>
  );

  // #1135: Settings keeps the photo in its Edit your details sheet only (the
  // approved board), so its profile fold leaves the panel out.
  const photoPanel = hidePhoto ? null : kitType ? kitPhotoPanel : legacyPhotoPanel;

  const formPanel = (
    <>
            <Row kitType={kitType} label="Full name" htmlFor="pp-name"><Input id="pp-name" data-testid="pp-name" className="h-11" value={player.full_name || ""} onChange={(e) => set("full_name", e.target.value)} /></Row>
            {/* #785 F1 fix — TWO two-up pairs, not one seven-cell grid. The
                first version of this fix wrapped Date of birth, Country,
                Club, Team, Batting style, Bowling style AND Playing role in
                one `grid-cols-2`, which auto-places by DOM order: Batting
                paired with Bowling (SetUp.dc.html:88-98 draws both full
                width) and Playing role orphaned alone in the left column
                (:100-105 draws it full width too) — the bowling
                placeholder clipped at 390px as a result. SetUp.dc.html:44/49
                draws exactly two two-up pairs — Date of birth | Country,
                Club | Team — and nothing else in this section is paired.
                Two separate grids, not `sm:grid-cols-2` (`sm` is 640px,
                which is why this was single-column at 390px before #785 at
                all). */}
            {/* #806 — the date input clipped its own calendar glyph in this
                pair at 390px. SetUp.dc.html:78 draws this row as TWO EQUAL
                columns (`grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: 10px`), :79-80 place the two cells inside it — the
                drawing does not draw a custom split, and this fix's first
                round (a fixed `11.5rem` date column) departed from that
                without saying so. Posted as a canvas round 5 question on
                #806: keep the pair (accepting a long country name gets
                truncated) or stack at phone width, as this fix now does.

                FIX ROUND 2 (first review, R1/R2): the fixed column was a
                net LOSS, not a net fix. `AccountProfileCard` DOES pass
                `frameless`, but the Settings CARD AROUND IT still has its
                own padding, and the shipped `Onboarding.jsx` wraps this
                component un-`frameless` in its own `<Card>` on top of THAT
                — both put this row at 286px, not OnboardingNew's 339px.
                179.5px (date's real min-content, measured by swapping
                `style.width` to `"min-content"` and back — a border-box
                number; see the E2E check's own comment on why that reads
                ~2px stricter than `clientWidth`, a padding-box number) plus
                a real country value's own need plus the 16px gap is MORE
                than 286px has, under any split at all: an equal split left
                the date glyph clipped WORSE than before this fix ever
                touched it (133px against 179.5px needed); the fixed
                `11.5rem` column "solved" that by taking the deficit out of
                Country instead (72px box against "New Zealand"'s 126px
                need — a 54px cut, worse than the defect this issue opened
                to fix). And Country is free text: `SetUp.dc.html` fills it
                with an EXAMPLE, not a bounded list, so even OnboardingNew's
                own 339px was never really safe — "United Kingdom" cut by
                25px, "Papua New Guinea" by 47px, on the fixed column's
                125px box. No split of any of these rows' widths holds both
                fields' real minimums for a real country name. Text size
                Large makes every one of these numbers worse, not better:
                the `Aa` control raises the ROOT font-size (17px -> 19px,
                `index.css`'s `html { font-size: var(--nw-base-font) }`),
                which every `rem`-based Tailwind class here answers to,
                INCLUDING a fixed `11.5rem` column — so a fixed column never
                had a fixed budget to begin with.

                THE FIX: stack instead of splitting, whenever the row
                cannot hold two columns of at least `MIN_PAIR_COLUMN_REM`
                each. `grid-template-columns: repeat(auto-fit,
                minmax(12.5rem, 1fr))` — CSS's own answer to "the container
                decides", not a container query (no card here needs any
                OTHER property, like font-size or padding, to respond to
                its own size — just column count — which `auto-fit` gives
                for free) and NOT keyed to the viewport (`sm:`/`md:` reads
                `window`, not this row's own available width, and this
                row's width already differs by host at the SAME viewport —
                339px vs 286px, above). `12.5rem` is `min-content`'s worst
                measured value (198.5px, Large text, this same reproduction)
                plus headroom, so TWO columns need `2*12.5rem + 16px` before
                `auto-fit` will ever offer two — at Default text that is
                441px, at Large 491px: MORE than any host measured here
                (max 339px), so this row stacks on every host, at every
                text size, at 390px, today. That is not a workaround; it is
                the honest answer to "not enough room for a real country
                name next to a real date field" being true everywhere this
                component currently renders. `auto-fit` is what gives a
                genuinely wider host (a tablet/desktop viewport; none of
                the three hosts add a width-based media query around this
                grid) the pair back automatically, once there is room for
                it — no flag, no breakpoint to keep in sync by hand.

                STACKED, EVERY HOST, MEASURED (390x844, deviceScaleFactor
                1): date `clientWidth` grows to the row's own full width in
                every case (194px OnboardingNew/Default, 217px Large; 194px
                Onboarding.jsx+Settings/Default, 217px Large) against the
                same 179.5px/198.5px min-content it always needed — never
                clipped. Country, stacked to the same full row width, holds
                every one of "New Zealand"/"United Kingdom"/"Papua New
                Guinea" at every host and both text sizes with room to
                spare (worst case: Settings, Large text, "Papua New
                Guinea" needs 193px against a 217px box — still clean). See
                #806 PR body for the full three-host table. */}
            <div className={kitType ? "grid gap-4 nw-kit-grid" : "grid gap-4"} style={{ gridTemplateColumns: "repeat(auto-fit, minmax(12.5rem, 1fr))" }}>
              <Row kitType={kitType} label="Date of birth" htmlFor="pp-dob"><Input id="pp-dob" data-testid="pp-dob" className="h-11" type="date" value={player.date_of_birth || ""} onChange={(e) => set("date_of_birth", e.target.value)} /></Row>
              <Row kitType={kitType} label="Country" htmlFor="pp-country"><Input id="pp-country" data-testid="pp-country" className="h-11" value={player.country || ""} onChange={(e) => set("country", e.target.value)} /></Row>
            </div>
            {/* #868 (D41) — "Your club"/"Your team" hints, drawn on
                SetUp.dc.html and ruled by Martin (decisions-2026-09-24.md
                D41): "Club and Team get hints." Scoped to `kitType`, the
                same OnboardingNew.jsx-only flag every other restyle on this
                file uses — Settings and the shipped Onboarding.jsx pass
                neither prop and render with no placeholder, unchanged.
                `nw-kit-input` (index.css) is the kit's own placeholder
                colour (`--nw-ink-muted`, #64748B); the bare `h-11` stays so
                `PlayerProfile.rhythm.test.js`'s h-11 pins (name/batting/
                role) are untouched. */}
            <div className={kitType ? "grid grid-cols-2 gap-4 nw-kit-grid" : "grid grid-cols-2 gap-4"}>
              <Row kitType={kitType} label="Club" htmlFor="pp-club">
                {kitType ? (
                  <Textarea id="pp-club" data-testid="pp-club" ref={clubRef} rows={1} className="nw-kit-input py-[11.5px]" style={{ minHeight: "var(--nw-size-control)", resize: "none", overflow: "hidden" }} enterKeyHint="done" placeholder="Your club" value={player.club || ""} onChange={(e) => set("club", e.target.value.replace(/\r?\n/g, " "))} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} />
                ) : (
                  <Input id="pp-club" data-testid="pp-club" className="h-11" value={player.club || ""} onChange={(e) => set("club", e.target.value)} />
                )}
              </Row>
              <Row kitType={kitType} label="Team" htmlFor="pp-school">
                {kitType ? (
                  <Textarea id="pp-school" data-testid="pp-school" ref={schoolRef} rows={1} className="nw-kit-input py-[11.5px]" style={{ minHeight: "var(--nw-size-control)", resize: "none", overflow: "hidden" }} enterKeyHint="done" placeholder="Your team" value={player.school || ""} onChange={(e) => set("school", e.target.value.replace(/\r?\n/g, " "))} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} />
                ) : (
                  <Input id="pp-school" data-testid="pp-school" className="h-11" value={player.school || ""} onChange={(e) => set("school", e.target.value)} />
                )}
              </Row>
            </div>
            <Row kitType={kitType} label="Batting style">
              <Select value={player.batting_style || ""} onValueChange={(v) => set("batting_style", v)}>
                <SelectTrigger ref={battingRef} data-testid="pp-batting" className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                {/* Review F1 (#830) — `SelectContent` renders through a Radix
                    Portal to `document.body`, OUTSIDE this component's own
                    `data-pp-kit-type` root (`data-testid="player-profile-page"`
                    below): the type-ramp CSS rules there are ordinary
                    descendant selectors, and a portalled subtree is not a
                    DOM descendant of the element it is logically owned by,
                    so the open option list never matched them — measured,
                    13.125/14.875/16.625px at Compact/Default/Large, still
                    moving, even with the control retired on this screen.
                    `data-pp-kit-type` is repeated here, directly on the
                    portalled root, for exactly that reason: `index.css`'s
                    `[data-pp-kit-type="true"] [role="option"]` rule needs no
                    `player-profile-page` ancestor to match it, because this
                    element never has one. */}
                <SelectContent {...(kitType ? { "data-pp-kit-type": "true" } : {})}>
                  <SelectItem value="Right-hand">Right-hand</SelectItem>
                  <SelectItem value="Left-hand">Left-hand</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row kitType={kitType} label="Bowling style" htmlFor="pp-bowling"><Input id="pp-bowling" data-testid="pp-bowling" className="h-11" value={player.bowling_style || ""} onChange={(e) => set("bowling_style", e.target.value)} placeholder="e.g. Right-arm fast-medium" /></Row>
            <Row kitType={kitType} label="Playing role">
              <Select value={player.playing_role || ""} onValueChange={(v) => set("playing_role", v)}>
                <SelectTrigger ref={roleRef} data-testid="pp-role" className="h-11"><SelectValue placeholder="Select" /></SelectTrigger>
                {/* Review F1 (#830) — same portal gap as Batting style above;
                    see that SelectContent's own comment. */}
                <SelectContent {...(kitType ? { "data-pp-kit-type": "true" } : {})}>
                  <SelectItem value="Batter">Batter</SelectItem>
                  <SelectItem value="Bowler">Bowler</SelectItem>
                  <SelectItem value="Batting All-rounder">Batting All-rounder</SelectItem>
                  <SelectItem value="Bowling All-rounder">Bowling All-rounder</SelectItem>
                  <SelectItem value="Wicket-keeper">Wicket-keeper</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            {!embedded && (
              <div className="pt-2">
                {saveError && (
                  <p
                    data-testid="pp-save-error"
                    role="alert"
                    style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13, lineHeight: 1.45, fontWeight: 600, color: "#0F172A", margin: "0 0 8px" }}
                  >
                    <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
                      <AlertTriangle size={15} color="#B42318" />
                    </span>
                    <span>{saveError}</span>
                  </p>
                )}
                <Button onClick={save} disabled={busy} data-testid="pp-save" className={kitType ? "h-11" : undefined} style={{ background: "#0B2E59" }}>
                  <Save size={16} className="mr-2" /> {busy ? "Saving…" : "Save changes"}
                </Button>
              </div>
            )}
    </>
  );

  return (
    <div
      className="space-y-6"
      data-testid="player-profile-page"
      {...(kitType ? { "data-pp-kit-type": "true" } : {})}
    >
      {!embedded && showHeading && (
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Your profile</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900 mt-1">Player Profile</h1>
        </div>
      )}

      {/* #508 — one single-column stack, the same shape CoachProfileCard
          uses, instead of the old photo-card-beside-a-form-card grid.
          Framed vs frameless is chrome only (#456's one-area merge — see
          the docstring): the shells differ, the panels do not, and each
          position keeps a stable element type per host so React never
          remounts the form mid-keystroke.

          #785 (review F3) — the PANEL ORDER is `photoLast`, a named prop,
          not inferred from `embedded && frameless`. That combination
          happened to select only `OnboardingNew.jsx` when this landed, but
          neither flag it is built from MEANS "photo last" — see the
          docstring for the decision this prop actually encodes. */}
      {frameless ? (
        // #868 (G5.4) — `space-y-4` is Tailwind `rem` (17px at the default
        // root, moving with the text-size control) against SetUp.dc.html's
        // drawn flat 14px rhythm between name / the two two-up grids /
        // batting / bowling / role / the photo card (all direct children
        // here, since `formPanel` is a Fragment — see its own JSX above).
        // `nw-kit-stack`'s sibling rule (index.css) reuses `.nw-page-stack`'s
        // own 14px token; the bare `space-y-4` class stays so nothing
        // outside `kitType` moves.
        <div className={kitType ? "space-y-4 nw-kit-stack" : "space-y-4"}>
          {photoLast ? (
            <>
              {formPanel}
              {photoPanel}
            </>
          ) : (
            <>
              {photoPanel}
              {formPanel}
            </>
          )}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-6 space-y-4">
            {photoLast ? (
              <>
                {formPanel}
                {photoPanel}
              </>
            ) : (
              <>
                {photoPanel}
                {formPanel}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Review round 3, final narrow round (A3), F9: `htmlFor` is optional —
// several `Row` callers wrap a `<Select>` (`pp-batting`/`pp-role`), not an
// `<Input>`, and are out of this fix round's own named scope — but every
// call site named in F9 now passes it, tying this ALREADY-real `<label>`
// (shadcn's own `Label`, `@radix-ui/react-label`'s `Root`) to its input,
// the same untied-real-element bug #902/#907 fixed elsewhere on this
// screen's own kit `Field`.
function Row({ label, htmlFor, children, kitType = false }) {
  // #508 — plain <Label> above a 1.5-gap control, the same rhythm the coach
  // editor (CoachProfileCard) uses, rather than the old tiny-uppercase label.
  // #868 (G5.4) — `mt-1.5` is Tailwind `rem` (6.4px at the default root,
  // moving with the text-size control) against SetUp.dc.html's drawn 4px
  // label-to-box gap. `nw-kit-tight` (index.css, kitType-scoped) overrides
  // it to `--nw-space-xtight` — the bare `mt-1.5` class stays so nothing
  // outside `kitType` (Settings, the shipped Onboarding.jsx) moves.
  return (
    <div>
      <Label htmlFor={htmlFor}>{label}</Label>
      <div className={kitType ? "mt-1.5 nw-kit-tight" : "mt-1.5"}>{children}</div>
    </div>
  );
}
